import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, resolveDirective as _resolveDirective, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, withModifiers as _withModifiers, createBlock as _createBlock, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "outreach-cue-cards__empty-state"
}
const _hoisted_2 = { class: "outreach-cue-cards__empty-content" }
const _hoisted_3 = {
  key: 0,
  class: "outreach-cue-cards__trigger-wrapper"
}
const _hoisted_4 = {
  type: "button",
  class: "outreach-cue-cards__trigger outreach-cue-cards__trigger--loading",
  disabled: ""
}
const _hoisted_5 = ["disabled"]
const _hoisted_6 = {
  key: 0,
  class: "outreach-cue-cards__dropdown"
}
const _hoisted_7 = { class: "outreach-cue-cards__dropdown-list" }
const _hoisted_8 = ["onClick"]
const _hoisted_9 = { class: "outreach-cue-cards__dropdown-option-label" }
const _hoisted_10 = ["onClick"]
const _hoisted_11 = {
  key: 2,
  class: "outreach-cue-cards__trigger-wrapper"
}
const _hoisted_12 = {
  type: "button",
  class: "outreach-cue-cards__trigger outreach-cue-cards__trigger--loading",
  disabled: ""
}
const _hoisted_13 = {
  key: 3,
  class: "outreach-cue-cards__trigger-wrapper"
}
const _hoisted_14 = { class: "outreach-cue-cards__error" }
const _hoisted_15 = { class: "outreach-cue-cards__error-content" }
const _hoisted_16 = { class: "outreach-cue-cards__error-actions" }
const _hoisted_17 = ["disabled"]
const _hoisted_18 = { class: "outreach-cue-cards__toolbar" }
const _hoisted_19 = ["disabled"]
const _hoisted_20 = {
  key: 0,
  class: "outreach-cue-cards__history"
}
const _hoisted_21 = {
  key: 0,
  class: "outreach-cue-cards__history-loading"
}
const _hoisted_22 = {
  key: 1,
  class: "outreach-cue-cards__history-empty"
}
const _hoisted_23 = {
  key: 2,
  class: "outreach-cue-cards__history-list"
}
const _hoisted_24 = ["onClick"]
const _hoisted_25 = { class: "outreach-cue-cards__history-item-left" }
const _hoisted_26 = { class: "outreach-cue-cards__history-item-time" }
const _hoisted_27 = { class: "outreach-cue-cards__history-item-preview" }
const _hoisted_28 = { class: "outreach-cue-cards__list" }

import { computed, ref, watch, onMounted, onBeforeUnmount } from "vue";
import { useToast } from "@/composables/ui/useToast";
import { useRouter } from "vue-router";
import { useBillingStatus, USAGE_BLOCKED_TOOLTIP } from "@/composables/billing/useBillingStatus";
import { Icon } from "@iconify/vue";
import { Button } from "@/components/ui";
import OutreachCueCard from "./OutreachCueCard.vue";
import AIGenerationService from "@/services/ai/ai-generation";
import {
  getCueCards,
  setCueCards,
  updateCueCardsSaved,
  clearCueCards,
  getHistoryCache,
  setHistoryCache,
  invalidateHistoryCache,
  fetchOutreachHistory,
  startBackgroundGeneration,
  registerMountedComponent,
  unregisterMountedComponent,
  isGenerationPending,
} from "@/store/outreachCueCards";
import type {
  OutreachCueCardsData,
  OutreachChannel,
  CueCardConfig,
  OutreachHistoryItem,
} from "@/types/outreach-cue-cards";
interface ICPOption {
  value: string;
  label: string;
}

interface Props {
  icpOptions: ICPOption[];
  contactId?: string;
  contactName?: string;
  fullWidth?: boolean;
  icpLoading?: boolean;
}


export default /*@__PURE__*/_defineComponent({
  ...{
  name: "OutreachCueCards",
},
  __name: 'OutreachCueCards',
  props: {
    icpOptions: {},
    contactId: { default: "" },
    contactName: { default: "" },
    fullWidth: { type: Boolean, default: false },
    icpLoading: { type: Boolean, default: false }
  },
  emits: ["use-content", "generation-complete", "generation-error"],
  setup(__props: any, { emit: __emit }) {



const props = __props;

const emit = __emit;

const router = useRouter();
const $toast = useToast();
const { showUsageBasedFeaturesModalOnce, isUsageBlocked } = useBillingStatus();

// Callbacks registered with the background generation system so it can notify
// this component when it's still mounted, or fall back to a toast when it isn't.
const generationCallbacks = {
  onSuccess() {
    const cached = getCueCards(props.contactId);
    if (!cached) return;
    currentRecordId.value = cached.recordId;
    isSaved.value = cached.isSaved;
    cueCardsData.value = cached.data;
    state.value = "ready";
    isPanelOpen.value = true;
    expandedCard.value = null;
    emit("generation-complete", cached.data);
  },
  async onError(err: Error) {
    console.error("Failed to generate outreach cue cards:", err);
    try {
      const items = await AIGenerationService.getOutreachHistory(props.contactId);
      if (items.length > 0) {
        const latest = items[0];
        cueCardsData.value = latest.data;
        currentRecordId.value = latest.id;
        isSaved.value = latest.isSaved;
        lastIcpId.value = latest.icpId;
        state.value = "ready";
        isPanelOpen.value = true;
        if (props.contactId && latest.icpId) {
          setCueCards(props.contactId, latest.data, latest.id, latest.icpId, latest.isSaved);
          invalidateHistoryCache(props.contactId);
        }
        return;
      }
    } catch {
      // fall through to error state
    }
    $toast?.error(
      (err as any)?.response?.data?.msg ||
        "Generation timed out. Click 'Check for Results' to retry."
    );
    state.value = "error";
    emit("generation-error", err);
  },
};

// State machine
const state = ref<"initial" | "checking" | "loading" | "ready" | "error">("initial");
const isDropdownOpen = ref(false);
const isPanelOpen = ref(true);
const expandedCard = ref<OutreachChannel | null>(null);
const cueCardsData = ref<OutreachCueCardsData | null>(null);
const dropdownRef = ref<HTMLElement | null>(null);
const readyRef = ref<HTMLElement | null>(null);
const readyTriggerRef = ref<HTMLElement | null>(null);
const panelPos = ref({ top: 0, left: 0, width: 0 });

// Save / History / Regenerate state
const currentRecordId = ref<string | null>(null);
const lastIcpId = ref<string | null>(null);
const isSaved = ref(false);
const isHistoryOpen = ref(false);
const historyItems = ref<OutreachHistoryItem[]>([]);
const isHistoryLoading = ref(false);

const sectionStyle = computed(() => ({
  width: props.fullWidth ? "100%" : "calc(100% + 24px)",
  marginLeft: props.fullWidth ? "0" : "-12px",
  marginRight: props.fullWidth ? "0" : "-12px",
  marginBottom: "8px",
  paddingLeft: props.fullWidth ? "0" : "12px",
  paddingRight: props.fullWidth ? "0" : "12px",
}));

const panelPositionStyle = computed(() => ({
  position: "fixed" as const,
  top: `${panelPos.value.top}px`,
  left: `${panelPos.value.left}px`,
  width: `${panelPos.value.width}px`,
}));

function updatePanelPosition() {
  const el = readyTriggerRef.value;
  if (!el) return;
  const rect = el.getBoundingClientRect();
  panelPos.value = {
    top: rect.bottom + 4,
    left: rect.left,
    width: rect.width,
  };
}

function togglePanel() {
  isPanelOpen.value = !isPanelOpen.value;
  if (isPanelOpen.value) {
    updatePanelPosition();
  }
}

const hasNoICPs = computed(() => !props.icpOptions || props.icpOptions.length <= 1);

const selectableOptions = computed(() => props.icpOptions.filter((opt) => opt.value !== ""));

const cueCardConfigs: CueCardConfig[] = [
  {
    channel: "call",
    title: "Cold Call Script",
    shortTitle: "Cold Call",
    icon: "mdi:phone-outline",
    badgeLabel: "Call",
    badgeVariant: "call",
  },
  {
    channel: "voicemail",
    title: "Voicemail Script",
    shortTitle: "Voicemail",
    icon: "mdi:voicemail",
    badgeLabel: "Voicemail",
    badgeVariant: "call",
  },
  {
    channel: "email",
    title: "Cold Email",
    shortTitle: "Cold Email",
    icon: "mdi:email-outline",
    badgeLabel: "Email",
    badgeVariant: "email",
  },
  {
    channel: "linkedin_connection",
    title: "LinkedIn Connection Note",
    shortTitle: "LinkedIn",
    icon: "mdi:linkedin",
    badgeLabel: "LinkedIn",
    badgeVariant: "linkedin",
  },
  {
    channel: "linkedin_followup",
    title: "LinkedIn Follow-up DM",
    shortTitle: "LinkedIn",
    icon: "mdi:linkedin",
    badgeLabel: "LinkedIn",
    badgeVariant: "linkedin",
  },
  {
    channel: "linkedin_inmail",
    title: "LinkedIn InMail",
    shortTitle: "InMail",
    icon: "mdi:email-fast-outline",
    badgeLabel: "InMail",
    badgeVariant: "linkedin",
  },
  {
    channel: "whatsapp",
    title: "WhatsApp Message",
    shortTitle: "WhatsApp",
    icon: "mdi:whatsapp",
    badgeLabel: "WhatsApp",
    badgeVariant: "whatsapp",
  },
  {
    channel: "sms",
    title: "SMS Message",
    shortTitle: "SMS",
    icon: "mdi:message-text-outline",
    badgeLabel: "SMS",
    badgeVariant: "sms",
  },
];

function toggleDropdown() {
  isDropdownOpen.value = !isDropdownOpen.value;
}

function toggleCard(channel: OutreachChannel) {
  expandedCard.value = expandedCard.value === channel ? null : channel;
}

async function generate(icpId: string, force = false) {
  await showUsageBasedFeaturesModalOnce("ai_generation", async () => {
    state.value = "loading";
    isDropdownOpen.value = false;
    lastIcpId.value = icpId;
    isHistoryOpen.value = false;

    startBackgroundGeneration(
      props.contactId,
      icpId,
      props.contactName,
      String(router.currentRoute.value.name ?? ""),
      { ...router.currentRoute.value.query },
      force || undefined
    );
  });
}

function handleICPSelect(icpId: string) {
  generate(icpId);
}

async function handleRegenerate() {
  if (!lastIcpId.value) {
    $toast?.warning("Please select an ICP first");
    return;
  }
  await generate(lastIcpId.value, true);
}

async function retryFromHistory() {
  if (!props.contactId) return;
  state.value = "loading";
  try {
    const items = await AIGenerationService.getOutreachHistory(props.contactId);
    if (items.length > 0) {
      const latest = items[0];
      cueCardsData.value = latest.data;
      currentRecordId.value = latest.id;
      isSaved.value = latest.isSaved;
      lastIcpId.value = latest.icpId;
      state.value = "ready";
      isPanelOpen.value = true;
      if (latest.icpId) {
        setCueCards(props.contactId, latest.data, latest.id, latest.icpId, latest.isSaved);
        invalidateHistoryCache(props.contactId);
      }
    } else {
      $toast?.warning("Results not ready yet. Please try again in a moment.");
      state.value = "error";
    }
  } catch {
    $toast?.error("Failed to check for results.");
    state.value = "error";
  }
}

function handleReset() {
  if (props.contactId) {
    clearCueCards(props.contactId);
  }
  state.value = "initial";
  isPanelOpen.value = true;
  isDropdownOpen.value = false;
  isHistoryOpen.value = false;
  cueCardsData.value = null;
  currentRecordId.value = null;
  lastIcpId.value = null;
  isSaved.value = false;
  expandedCard.value = null;
}

async function handleSave() {
  if (!currentRecordId.value) {
    $toast?.warning("No content to save");
    return;
  }

  try {
    const result = await AIGenerationService.toggleSaveOutreach(currentRecordId.value);
    isSaved.value = result.isSaved;
    if (props.contactId) {
      updateCueCardsSaved(props.contactId, result.isSaved);
    }
    $toast?.success(result.isSaved ? "Saved" : "Removed from saved");
  } catch (err: any) {
    console.error("Failed to toggle save:", err);
    $toast?.error("Failed to save content");
  }
}

async function handleHistory() {
  if (isHistoryOpen.value) {
    isHistoryOpen.value = false;
    return;
  }

  isHistoryOpen.value = true;

  const cached = props.contactId ? getHistoryCache(props.contactId) : null;
  if (cached) {
    historyItems.value = cached;
    return;
  }

  isHistoryLoading.value = true;

  try {
    const items = await AIGenerationService.getOutreachHistory(props.contactId);
    historyItems.value = items;
    if (props.contactId) {
      setHistoryCache(props.contactId, items);
    }
  } catch (err: any) {
    console.error("Failed to load history:", err);
    $toast?.error("Failed to load history");
    historyItems.value = [];
  } finally {
    isHistoryLoading.value = false;
  }
}

function loadHistoryItem(item: OutreachHistoryItem) {
  cueCardsData.value = item.data;
  currentRecordId.value = item.id;
  isSaved.value = item.isSaved;
  expandedCard.value = null;
  if (props.contactId && item.icpId) {
    setCueCards(props.contactId, item.data, item.id, item.icpId, item.isSaved);
  }
}

function formatRelativeTime(dateStr: string): string {
  const now = Date.now();
  const date = new Date(dateStr).getTime();
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60000);

  if (diffMin < 1) return "Just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHrs = Math.floor(diffMin / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

function getHistoryPreview(item: OutreachHistoryItem): string {
  const subject = item.data?.email?.subject;
  if (subject) {
    return subject.length > 50 ? subject.slice(0, 50) + "..." : subject;
  }
  return "Outreach content";
}

function handleCardCopy(_channel: string) {
  $toast?.success("Copied to clipboard");
}

function handleUseContent(payload: {
  channel: string;
  content: string;
  subject?: string;
  callSteps?: { label: string; content: string; color: string }[];
}) {
  const plainTextChannels = [
    "whatsapp",
    "sms",
    "linkedin_connection",
    "linkedin_followup",
    "linkedin_inmail",
  ];
  if (plainTextChannels.includes(payload.channel)) {
    payload.content = payload.content
      .replace(/<\/p>\s*<p[^>]*>/gi, "\n\n")
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/(?:p|div|h[1-6]|li|blockquote)>/gi, "\n\n")
      .replace(/<[^>]*>/g, "")
      .replace(/\n{3,}/g, "\n\n")
      .trim();
  }
  if (payload.callSteps) {
    payload.callSteps = payload.callSteps.map((step) => ({
      ...step,
      content: step.content.replace(/<br\s*\/?>/gi, "\n").replace(/<[^>]*>/g, ""),
    }));
  }

  isPanelOpen.value = false;
  expandedCard.value = null;
  emit("use-content", payload);
}

function goToICPSettings() {
  router.push({ path: "/settings/general", query: { tab: "icp" } });
}

function goToEditICP(icpId: string) {
  router.push({ path: "/settings/general", query: { tab: "icp", icpId } });
}

function goToAddICP() {
  router.push({ path: "/settings/general", query: { tab: "icp" } });
}

function handleClickOutside(event: MouseEvent) {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target as Node)) {
    isDropdownOpen.value = false;
  }
}

async function initFromCacheOrHistory(contactId: string) {
  if (!contactId || hasNoICPs.value) return;

  // 1. Check in-memory cache
  const cached = getCueCards(contactId);
  if (cached) {
    cueCardsData.value = cached.data;
    currentRecordId.value = cached.recordId;
    lastIcpId.value = cached.icpId;
    isSaved.value = cached.isSaved;
    state.value = "ready";
    isPanelOpen.value = false;
    return;
  }

  // 2. Fetch from API (deduped across instances)
  state.value = "checking";
  try {
    const items = await fetchOutreachHistory(contactId);
    // Guard: bail if contactId changed or user already took action
    if (props.contactId !== contactId || state.value !== "checking") return;
    if (items.length > 0) {
      const latest = items[0];
      cueCardsData.value = latest.data;
      currentRecordId.value = latest.id;
      isSaved.value = latest.isSaved;
      lastIcpId.value = latest.icpId;
      state.value = "ready";
      isPanelOpen.value = false;
      if (latest.icpId) {
        setCueCards(contactId, latest.data, latest.id, latest.icpId, latest.isSaved);
      }
    } else {
      state.value = "initial";
    }
  } catch (err) {
    console.warn("[OutreachCueCards] Failed to load history on init:", err);
    // Fall back to "initial" — cache watcher will pick up data if another panel succeeds
    if (props.contactId === contactId && state.value === "checking") {
      state.value = "initial";
    }
  }
}

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
  registerMountedComponent(props.contactId, generationCallbacks);
  if (isGenerationPending(props.contactId)) {
    state.value = "loading";
  } else {
    initFromCacheOrHistory(props.contactId);
  }
});

watch(
  () => props.contactId,
  (newId, oldId) => {
    if (newId && newId !== oldId) {
      // Reset for new contact
      state.value = "initial";
      cueCardsData.value = null;
      currentRecordId.value = null;
      lastIcpId.value = null;
      isSaved.value = false;
      expandedCard.value = null;
      isDropdownOpen.value = false;
      isHistoryOpen.value = false;
      historyItems.value = [];
      initFromCacheOrHistory(newId);
    }
  }
);

watch(
  () => getCueCards(props.contactId),
  (cached) => {
    if (!cached) return;
    const wasLoading = state.value === "loading";
    const isStale =
      wasLoading ||
      state.value === "initial" ||
      state.value === "checking" ||
      state.value === "error";
    const isNewRecord = state.value === "ready" && cached.recordId !== currentRecordId.value;
    if (isStale || isNewRecord) {
      cueCardsData.value = cached.data;
      currentRecordId.value = cached.recordId;
      lastIcpId.value = cached.icpId;
      isSaved.value = cached.isSaved;
      state.value = "ready";
      if (wasLoading) {
        isPanelOpen.value = true;
        expandedCard.value = null;
        emit("generation-complete", cached.data);
      } else if (!isNewRecord) {
        isPanelOpen.value = false;
      }
    }
  }
);

watch(hasNoICPs, (noICPs, wasNoICPs) => {
  if (!noICPs && wasNoICPs && props.contactId && state.value === "initial") {
    initFromCacheOrHistory(props.contactId);
  }
});

onBeforeUnmount(() => {
  document.removeEventListener("click", handleClickOutside);
  unregisterMountedComponent(props.contactId);
});

return (_ctx: any,_cache: any) => {
  const _directive_tooltip = _resolveDirective("tooltip")!

  return (_openBlock(), _createElementBlock("div", {
    class: "outreach-cue-cards",
    style: _normalizeStyle(sectionStyle.value)
  }, [
    (hasNoICPs.value && !props.icpLoading)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createElementVNode("div", _hoisted_2, [
            _createVNode(_unref(Icon), {
              icon: "mdi:sparkles",
              class: "outreach-cue-cards__empty-icon"
            }),
            _cache[0] || (_cache[0] = _createElementVNode("span", { class: "outreach-cue-cards__empty-text" }, "Set up ICP for AI personalization", -1))
          ]),
          _createVNode(_unref(Button), {
            variant: "primary",
            size: "sm",
            icon: "mdi:arrow-right",
            "icon-position": "right",
            class: "outreach-cue-cards__empty-btn",
            onClick: goToICPSettings
          }, {
            default: _withCtx(() => [...(_cache[1] || (_cache[1] = [
              _createTextVNode(" Set up ICP ", -1)
            ]))]),
            _: 1
          })
        ]))
      : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
          ((state.value === 'initial' && props.icpLoading) || state.value === 'checking')
            ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                _createElementVNode("button", _hoisted_4, [
                  _createVNode(_unref(Icon), {
                    icon: "mdi:loading",
                    class: "outreach-cue-cards__spinner"
                  }),
                  _cache[2] || (_cache[2] = _createElementVNode("span", null, "Loading...", -1))
                ])
              ]))
            : (state.value === 'initial')
              ? (_openBlock(), _createElementBlock("div", {
                  key: 1,
                  class: "outreach-cue-cards__trigger-wrapper",
                  ref_key: "dropdownRef",
                  ref: dropdownRef
                }, [
                  _withDirectives((_openBlock(), _createElementBlock("button", {
                    type: "button",
                    class: "outreach-cue-cards__trigger outreach-cue-cards__trigger--initial",
                    disabled: _unref(isUsageBlocked),
                    onClick: toggleDropdown
                  }, [
                    _createVNode(_unref(Icon), {
                      icon: "mdi:sparkles",
                      class: "outreach-cue-cards__trigger-icon"
                    }),
                    _cache[3] || (_cache[3] = _createElementVNode("span", null, "Generate All Outreach Assets", -1)),
                    _createVNode(_unref(Icon), {
                      icon: "mdi:chevron-down",
                      class: _normalizeClass(["outreach-cue-cards__trigger-chevron", { 'outreach-cue-cards__trigger-chevron--open': isDropdownOpen.value }])
                    }, null, 8, ["class"])
                  ], 8, _hoisted_5)), [
                    [_directive_tooltip, { content: _unref(USAGE_BLOCKED_TOOLTIP), disabled: !_unref(isUsageBlocked) }]
                  ]),
                  (isDropdownOpen.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                        _cache[5] || (_cache[5] = _createElementVNode("div", { class: "outreach-cue-cards__dropdown-header" }, "Select ICP", -1)),
                        _createElementVNode("div", _hoisted_7, [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(selectableOptions.value, (option) => {
                            return (_openBlock(), _createElementBlock("button", {
                              key: option.value,
                              type: "button",
                              class: "outreach-cue-cards__dropdown-option",
                              onClick: ($event: any) => (handleICPSelect(option.value))
                            }, [
                              _createElementVNode("span", _hoisted_9, _toDisplayString(option.label), 1),
                              _createElementVNode("span", {
                                class: "outreach-cue-cards__dropdown-option-edit",
                                onClick: _withModifiers(($event: any) => (goToEditICP(option.value)), ["stop"]),
                                title: "Edit ICP"
                              }, [
                                _createVNode(_unref(Icon), { icon: "mdi:pencil-outline" })
                              ], 8, _hoisted_10)
                            ], 8, _hoisted_8))
                          }), 128))
                        ]),
                        _cache[6] || (_cache[6] = _createElementVNode("div", { class: "outreach-cue-cards__dropdown-divider" }, null, -1)),
                        _createElementVNode("button", {
                          type: "button",
                          class: "outreach-cue-cards__dropdown-add",
                          onClick: goToAddICP
                        }, [
                          _createVNode(_unref(Icon), {
                            icon: "mdi:plus",
                            class: "outreach-cue-cards__dropdown-add-icon"
                          }),
                          _cache[4] || (_cache[4] = _createElementVNode("span", null, "Add New ICP", -1))
                        ])
                      ]))
                    : _createCommentVNode("", true)
                ], 512))
              : (state.value === 'loading')
                ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                    _createElementVNode("button", _hoisted_12, [
                      _createVNode(_unref(Icon), {
                        icon: "mdi:loading",
                        class: "outreach-cue-cards__spinner"
                      }),
                      _cache[7] || (_cache[7] = _createElementVNode("span", null, "Generating...", -1))
                    ])
                  ]))
                : (state.value === 'error')
                  ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                      _createElementVNode("div", _hoisted_14, [
                        _createElementVNode("div", _hoisted_15, [
                          _createVNode(_unref(Icon), {
                            icon: "mdi:alert-circle-outline",
                            class: "outreach-cue-cards__error-icon"
                          }),
                          _cache[8] || (_cache[8] = _createElementVNode("span", { class: "outreach-cue-cards__error-text" }, " Generation timed out. Results may still be processing. ", -1))
                        ]),
                        _createElementVNode("div", _hoisted_16, [
                          _createElementVNode("button", {
                            type: "button",
                            class: "outreach-cue-cards__error-btn outreach-cue-cards__error-btn--retry",
                            onClick: retryFromHistory
                          }, [
                            _createVNode(_unref(Icon), { icon: "mdi:arrow-clockwise" }),
                            _cache[9] || (_cache[9] = _createElementVNode("span", null, "Check for Results", -1))
                          ]),
                          _withDirectives((_openBlock(), _createElementBlock("button", {
                            type: "button",
                            class: "outreach-cue-cards__error-btn outreach-cue-cards__error-btn--regen",
                            disabled: _unref(isUsageBlocked),
                            onClick: handleRegenerate
                          }, [
                            _createVNode(_unref(Icon), { icon: "mdi:refresh" }),
                            _cache[10] || (_cache[10] = _createElementVNode("span", null, "Regenerate", -1))
                          ], 8, _hoisted_17)), [
                            [_directive_tooltip, { content: _unref(USAGE_BLOCKED_TOOLTIP), disabled: !_unref(isUsageBlocked) }]
                          ])
                        ])
                      ])
                    ]))
                  : (state.value === 'ready')
                    ? (_openBlock(), _createElementBlock("div", {
                        key: 4,
                        class: "outreach-cue-cards__ready-wrapper",
                        ref_key: "readyRef",
                        ref: readyRef
                      }, [
                        _createElementVNode("button", {
                          ref_key: "readyTriggerRef",
                          ref: readyTriggerRef,
                          type: "button",
                          class: "outreach-cue-cards__trigger outreach-cue-cards__trigger--ready",
                          onClick: togglePanel
                        }, [
                          _createVNode(_unref(Icon), {
                            icon: "mdi:check-circle",
                            class: "outreach-cue-cards__trigger-icon"
                          }),
                          _cache[11] || (_cache[11] = _createElementVNode("span", null, "Outreach Cue Cards", -1)),
                          _createVNode(_unref(Icon), {
                            icon: "mdi:chevron-down",
                            class: _normalizeClass(["outreach-cue-cards__trigger-chevron", { 'outreach-cue-cards__trigger-chevron--open': isPanelOpen.value }])
                          }, null, 8, ["class"])
                        ], 512),
                        (isPanelOpen.value)
                          ? (_openBlock(), _createElementBlock("div", {
                              key: 0,
                              class: "outreach-cue-cards__panel",
                              style: _normalizeStyle(panelPositionStyle.value)
                            }, [
                              _createElementVNode("div", _hoisted_18, [
                                _createElementVNode("button", {
                                  type: "button",
                                  class: "outreach-cue-cards__toolbar-btn",
                                  onClick: handleSave
                                }, [
                                  _createVNode(_unref(Icon), {
                                    icon: isSaved.value ? 'mdi:content-save' : 'mdi:content-save-outline'
                                  }, null, 8, ["icon"]),
                                  _createElementVNode("span", null, _toDisplayString(isSaved.value ? "Saved" : "Save"), 1)
                                ]),
                                _createElementVNode("button", {
                                  type: "button",
                                  class: "outreach-cue-cards__toolbar-btn",
                                  onClick: handleHistory
                                }, [
                                  _createVNode(_unref(Icon), { icon: "mdi:history" }),
                                  _cache[12] || (_cache[12] = _createElementVNode("span", null, "History", -1))
                                ]),
                                _withDirectives((_openBlock(), _createElementBlock("button", {
                                  type: "button",
                                  class: "outreach-cue-cards__toolbar-btn",
                                  disabled: _unref(isUsageBlocked),
                                  onClick: handleRegenerate
                                }, [
                                  _createVNode(_unref(Icon), { icon: "mdi:refresh" }),
                                  _cache[13] || (_cache[13] = _createElementVNode("span", null, "Regenerate", -1))
                                ], 8, _hoisted_19)), [
                                  [_directive_tooltip, { content: _unref(USAGE_BLOCKED_TOOLTIP), disabled: !_unref(isUsageBlocked) }]
                                ]),
                                _createElementVNode("button", {
                                  type: "button",
                                  class: "outreach-cue-cards__toolbar-btn outreach-cue-cards__toolbar-btn--reset",
                                  onClick: handleReset,
                                  title: "Change ICP"
                                }, [
                                  _createVNode(_unref(Icon), { icon: "mdi:restart" })
                                ])
                              ]),
                              (isHistoryOpen.value)
                                ? (_openBlock(), _createElementBlock("div", _hoisted_20, [
                                    (isHistoryLoading.value)
                                      ? (_openBlock(), _createElementBlock("div", _hoisted_21, [
                                          _createVNode(_unref(Icon), {
                                            icon: "mdi:loading",
                                            class: "outreach-cue-cards__spinner"
                                          }),
                                          _cache[14] || (_cache[14] = _createElementVNode("span", null, "Loading history...", -1))
                                        ]))
                                      : (historyItems.value.length === 0)
                                        ? (_openBlock(), _createElementBlock("div", _hoisted_22, [...(_cache[15] || (_cache[15] = [
                                            _createElementVNode("span", null, "No past generations found", -1)
                                          ]))]))
                                        : (_openBlock(), _createElementBlock("div", _hoisted_23, [
                                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(historyItems.value, (item) => {
                                              return (_openBlock(), _createElementBlock("button", {
                                                key: item.id,
                                                type: "button",
                                                class: _normalizeClass(["outreach-cue-cards__history-item", { 'outreach-cue-cards__history-item--active': item.id === currentRecordId.value }]),
                                                onClick: ($event: any) => (loadHistoryItem(item))
                                              }, [
                                                _createElementVNode("div", _hoisted_25, [
                                                  _createElementVNode("span", _hoisted_26, _toDisplayString(formatRelativeTime(item.createdAt)), 1),
                                                  _createElementVNode("span", _hoisted_27, _toDisplayString(getHistoryPreview(item)), 1)
                                                ]),
                                                (item.isSaved)
                                                  ? (_openBlock(), _createBlock(_unref(Icon), {
                                                      key: 0,
                                                      icon: "mdi:content-save",
                                                      class: "outreach-cue-cards__history-item-saved"
                                                    }))
                                                  : _createCommentVNode("", true)
                                              ], 10, _hoisted_24))
                                            }), 128))
                                          ]))
                                  ]))
                                : _createCommentVNode("", true),
                              _createElementVNode("div", _hoisted_28, [
                                (_openBlock(), _createElementBlock(_Fragment, null, _renderList(cueCardConfigs, (card) => {
                                  return _createVNode(OutreachCueCard, {
                                    key: card.channel,
                                    config: card,
                                    data: cueCardsData.value!,
                                    "is-expanded": expandedCard.value === card.channel,
                                    onToggle: ($event: any) => (toggleCard(card.channel)),
                                    onCopy: handleCardCopy,
                                    onUseContent: handleUseContent
                                  }, null, 8, ["config", "data", "is-expanded", "onToggle"])
                                }), 64))
                              ])
                            ], 4))
                          : _createCommentVNode("", true)
                      ], 512))
                    : _createCommentVNode("", true)
        ], 64))
  ], 4))
}
}

})